import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// US federal holidays for illustration; users can also pick UK/CA/DE/IN or add "None".
// Represented as { name, monthIdx (0-based), day OR nth+dow rule }
const HOLIDAY_RULES = {
  US: [
    { n:"New Year's Day", month:0, day:1 },
    { n:"Martin Luther King Jr. Day", month:0, nth:3, dow:1 },
    { n:"Presidents' Day", month:1, nth:3, dow:1 },
    { n:"Memorial Day", month:4, nth:-1, dow:1 },
    { n:"Independence Day", month:6, day:4 },
    { n:"Labor Day", month:8, nth:1, dow:1 },
    { n:"Columbus Day", month:9, nth:2, dow:1 },
    { n:"Veterans Day", month:10, day:11 },
    { n:"Thanksgiving", month:10, nth:4, dow:4 },
    { n:"Christmas Day", month:11, day:25 }
  ],
  UK: [
    { n:"New Year's Day", month:0, day:1 },
    { n:"Good Friday", month:3, day:15 }, // approximation
    { n:"May Bank Holiday", month:4, nth:1, dow:1 },
    { n:"Spring Bank Holiday", month:4, nth:-1, dow:1 },
    { n:"Summer Bank Holiday", month:7, nth:-1, dow:1 },
    { n:"Christmas Day", month:11, day:25 },
    { n:"Boxing Day", month:11, day:26 }
  ],
  CA: [
    { n:"New Year's Day", month:0, day:1 },
    { n:"Canada Day", month:6, day:1 },
    { n:"Labour Day", month:8, nth:1, dow:1 },
    { n:"Thanksgiving", month:9, nth:2, dow:1 },
    { n:"Christmas Day", month:11, day:25 }
  ],
  IN: [
    { n:"Republic Day", month:0, day:26 },
    { n:"Independence Day", month:7, day:15 },
    { n:"Gandhi Jayanti", month:9, day:2 },
    { n:"Christmas Day", month:11, day:25 }
  ],
  None: []
};

function nthWeekday(year, month, nth, dow) {
  if (nth > 0) {
    const first = new Date(year, month, 1);
    const shift = (dow - first.getDay() + 7) % 7;
    return new Date(year, month, 1 + shift + (nth - 1) * 7);
  }
  // last dow of month
  const last = new Date(year, month + 1, 0);
  const shift = (last.getDay() - dow + 7) % 7;
  return new Date(year, month, last.getDate() - shift);
}

function holidaysBetween(start, end, country) {
  const rules = HOLIDAY_RULES[country] || [];
  const results = [];
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
    for (const r of rules) {
      const d = r.day ? new Date(y, r.month, r.day) : nthWeekday(y, r.month, r.nth, r.dow);
      if (d >= start && d <= end) results.push({ ...r, date: d });
    }
  }
  return results;
}

function isWeekend(d) { const g = d.getDay(); return g === 0 || g === 6; }

export default function DateCalc() {
  const [start, setStart] = useState(() => new Date().toISOString().slice(0,10));
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0,10));
  const [addDate, setAddDate] = useState(() => new Date().toISOString().slice(0,10));
  const [days, setDays] = useState(30);
  const [op, setOp] = useState('+');

  const [country, setCountry] = useState('US');
  const [countHolidays, setCountHolidays] = useState(true);
  const [businessOnly, setBusinessOnly] = useState(false); // exclude weekends
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  // Custom holidays: [{ name, date: 'YYYY-MM-DD' }]
  const [customHolidays, setCustomHolidays] = useState([]);
  const [cName, setCName] = useState('');
  const [cDate, setCDate] = useState('');
  const addCustom = () => { if (cName && cDate) { setCustomHolidays(l => [...l, { name: cName, date: cDate }].sort((a,b) => a.date.localeCompare(b.date))); setCName(''); setCDate(''); } };
  const removeCustom = (i) => setCustomHolidays(l => l.filter((_,ix) => ix !== i));
  const customSetKey = customHolidays.map(h => h.date).join('|');

  const diff = useMemo(() => {
    const s = new Date(start), e = new Date(end);
    const totalDays = Math.round((e - s) / 86400000);
    let y = e.getFullYear() - s.getFullYear();
    let m = e.getMonth() - s.getMonth();
    let d = e.getDate() - s.getDate();
    if (d < 0) { m--; d += new Date(e.getFullYear(), e.getMonth(), 0).getDate(); }
    if (m < 0) { y--; m += 12; }
    const hs = countHolidays ? holidaysBetween(s, e, country) : [];
    // count business days between (excl weekends and optionally holidays)
    let biz = 0; const cursor = new Date(s);
    while (cursor <= e) {
      if (!isWeekend(cursor)) {
        const isHoliday = hs.some(h => h.date.toDateString() === cursor.toDateString());
        if (!isHoliday) biz++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return { totalDays, y, m, d, weeks: Math.floor(totalDays/7), holidays: hs, biz };
  }, [start, end, country, countHolidays]);

  const shifted = useMemo(() => {
    const d = new Date(addDate);
    const cnt = Math.abs(+days || 0);
    const step = op === '+' ? 1 : -1;
    if (!businessOnly && !excludeHolidays) {
      d.setDate(d.getDate() + step * cnt);
      return d;
    }
    // Advance skipping weekends and/or holidays
    let remaining = cnt;
    const year = d.getFullYear();
    const hSet = new Set(
      excludeHolidays ? [
        ...HOLIDAY_RULES[country].flatMap(r => {
          const arr = [];
          for (let y = year - 1; y <= year + 5; y++) {
            const dd = r.day ? new Date(y, r.month, r.day) : nthWeekday(y, r.month, r.nth, r.dow);
            arr.push(dd.toDateString());
          }
          return arr;
        }),
        ...customHolidays.map(h => new Date(h.date).toDateString()),
      ] : []
    );
    while (remaining > 0) {
      d.setDate(d.getDate() + step);
      const skipWk = businessOnly && isWeekend(d);
      const skipHoliday = excludeHolidays && hSet.has(d.toDateString());
      if (!skipWk && !skipHoliday) remaining--;
    }
    return d;
  }, [addDate, days, op, businessOnly, excludeHolidays, country, customSetKey]);

  return (
    <CalcShell look="cnet" title="Date Calculator" subtitle="Find the difference between dates or add / subtract days — with optional holiday and business-day settings.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="font-bold mb-2">Difference between two dates</h3>
          <div className="field"><label>Start Date</label><input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="date-start"/></div>
          <div className="field"><label>End Date</label><input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="date-end"/></div>
          <div className="field"><label>Holiday Country</label>
            <select value={country} onChange={e => setCountry(e.target.value)}>{Object.keys(HOLIDAY_RULES).map(k => <option key={k}>{k}</option>)}</select>
          </div>
          <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={countHolidays} onChange={e => setCountHolidays(e.target.checked)}/> Include holiday list in results</label>
          <table className="data-table"><tbody>
            <tr><td>Result</td><td className="text-right font-semibold">{diff.y}y {diff.m}m {diff.d}d</td></tr>
            <tr><td>Total days</td><td className="text-right">{diff.totalDays}</td></tr>
            <tr><td>Total weeks</td><td className="text-right">{diff.weeks}</td></tr>
            <tr><td>Business days (excl. weekends &amp; holidays)</td><td className="text-right font-semibold">{diff.biz}</td></tr>
          </tbody></table>
          {countHolidays && diff.holidays.length > 0 && (
            <div className="mt-3">
              <h4 className="text-sm font-semibold mb-1">Holidays in range ({diff.holidays.length})</h4>
              <ul className="text-xs text-slate-600 space-y-1">
                {diff.holidays.slice(0, 12).map((h, i) => <li key={i}>{h.date.toDateString()} — {h.n}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-4 border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="text-sm font-semibold mb-2">Add custom holiday</div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex flex-col text-xs">
                <label className="text-slate-500 mb-0.5">Name</label>
                <input value={cName} onChange={e => setCName(e.target.value)} placeholder="e.g. Founder's Day" className="px-2 py-1 border rounded" data-testid="date-custom-name" />
              </div>
              <div className="flex flex-col text-xs">
                <label className="text-slate-500 mb-0.5">Date</label>
                <input type="date" value={cDate} onChange={e => setCDate(e.target.value)} className="px-2 py-1 border rounded" data-testid="date-custom-date" />
              </div>
              <button onClick={addCustom} className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs" data-testid="date-custom-add">Add</button>
            </div>
            {customHolidays.length > 0 && (
              <ul className="mt-2 text-xs space-y-1" data-testid="date-custom-list">
                {customHolidays.map((h, i) => (
                  <li key={h.date + i} className="flex items-center justify-between bg-white px-2 py-1 rounded">
                    <span>{h.date} — {h.name}</span>
                    <button onClick={() => removeCustom(i)} className="text-red-500" data-testid={`date-custom-remove-${i}`}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div>
          <h3 className="font-bold mb-2">Add / subtract days</h3>
          <div className="field"><label>Start Date</label><input type="date" value={addDate} onChange={e => setAddDate(e.target.value)} data-testid="date-add"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="field"><label>Operation</label><select value={op} onChange={e => setOp(e.target.value)}><option>+</option><option>-</option></select></div>
            <div className="field"><label>Days</label><input type="number" value={days} onChange={e => setDays(+e.target.value || 0)} data-testid="date-days"/></div>
          </div>
          <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" checked={businessOnly} onChange={e => setBusinessOnly(e.target.checked)} data-testid="date-biz"/> Business days only (skip weekends)</label>
          <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={excludeHolidays} onChange={e => setExcludeHolidays(e.target.checked)}/> Also skip {country} holidays</label>
          <div className="result-panel mt-2"><div className="result-title">Result Date</div><div className="result-value text-2xl" data-testid="date-result">{shifted.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div></div>
        </div>
      </div>
    </CalcShell>
  );
}
